import { useMemo } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { motion } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { fetchInSeason } from "../lib/api";
import { Reveal, StaggerGroup, riseItem } from "./Reveal";

// "What's in supply this month" — reads the live calendar for the current month.
export default function InSeasonNow() {
  const month = useMemo(() => new Date().getMonth() + 1, []);
  const { data } = useSWR(["in-season", month], () => fetchInSeason(month));

  return (
    <section data-testid="in-season" className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
      <Reveal className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl font-medium leading-[1.05] text-forest md:text-5xl">
            What's on the desk this{" "}
            <span className="italic text-gold" data-testid="in-season-month">
              {data?.month_name || "…"}
            </span>
          </h2>
          <p className="mt-5 max-w-xl text-forest-light">
            Updated live from the sourcing calendar —{" "}
            <span className="font-mono text-forest">
              {data ? data.total_in_season : "…"}
            </span>{" "}
            varieties across{" "}
            <span className="font-mono text-forest">
              {data ? data.families_in_season : "…"}
            </span>{" "}
            fruit families are in their market window today
            {data && data.total_at_peak > 0 && (
              <>
                {" "}—{" "}
                <span className="font-mono text-gold">{data.total_at_peak}</span> at
                their <span className="text-gold">absolute peak</span>
              </>
            )}
            .
          </p>
        </div>
        <Link to="/calendar" className="group flex flex-none items-center gap-2 self-start text-forest md:self-end">
          <span className="overline">Full calendar</span>
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>

      <StaggerGroup className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {(data?.families || []).map((f) => (
          <motion.div key={f.slug} variants={riseItem}>
            <Link
              to={`/calendar?fruit=${f.slug}`}
              data-testid={`in-season-${f.slug}`}
              className="group relative flex h-full items-center gap-4 rounded-sm border border-border bg-cream-200 p-4 transition-colors duration-500 hover:border-forest/40"
            >
              {f.peak > 0 && (
                <span
                  data-testid={`in-season-peak-${f.slug}`}
                  className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-gold px-2 py-0.5 font-mono text-[0.52rem] font-medium uppercase tracking-wider text-forest"
                >
                  <Star className="h-2.5 w-2.5 fill-forest" />
                  {f.peak} peak
                </span>
              )}
              <span className="flex h-16 w-16 flex-none items-center justify-center rounded-sm bg-white">
                <img src={f.image} alt={f.name} loading="lazy" className="max-h-12 w-auto object-contain" />
              </span>
              <span className="block min-w-0 flex-1">
                <span className="block font-display text-base leading-tight text-forest sm:text-lg lg:text-xl break-words">{f.name}</span>
                <span className="mt-1 block font-mono text-[0.62rem] uppercase tracking-wide text-forest-muted">
                  {f.count} in season
                </span>
                <span className="mt-1.5 flex items-center gap-2 font-mono text-[0.58rem] uppercase tracking-wider">
                  {f.indian > 0 && (
                    <span className="flex items-center gap-1 text-harvest">
                      <span className="h-1.5 w-1.5 rounded-full bg-harvest" />{f.indian} IN
                    </span>
                  )}
                  {f.imported > 0 && (
                    <span className="flex items-center gap-1 text-terracotta">
                      <span className="h-1.5 w-1.5 rounded-full bg-terracotta" />{f.imported} IMP
                    </span>
                  )}
                </span>
              </span>
            </Link>
          </motion.div>
        ))}
      </StaggerGroup>
    </section>
  );
}
